import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const VELUNE_CART_COUNT_SYNC_EVENT = 'velune:cart-count-sync';

/**
 * VELUNE — CART DRAWER PERFORMANCE V4
 *
 * Goals:
 * - Open the cart drawer as soon as CartAddEvent is received.
 * - Never open on failed add-to-cart events.
 * - Keep non-critical work out of the opening frame.
 * - Avoid duplicate open calls and redundant layout work.
 * - Preserve mobile history/back behavior and accessibility.
 * - Restore the final cart-count announcement after background cart sync.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #postOpenFrameOne = 0;

  /** @type {number} */
  #postOpenFrameTwo = 0;

  /** @type {number} */
  #announceFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(VELUNE_CART_COUNT_SYNC_EVENT, this.#handleCartCountSync);

    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(VELUNE_CART_COUNT_SYNC_EVENT, this.#handleCartCountSync);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.#historyAbortController?.abort();
    this.#historyAbortController = null;

    this.#cancelDeferredWork();
  }

  #cancelDeferredWork() {
    if (this.#postOpenFrameOne) {
      cancelAnimationFrame(this.#postOpenFrameOne);
      this.#postOpenFrameOne = 0;
    }

    if (this.#postOpenFrameTwo) {
      cancelAnimationFrame(this.#postOpenFrameTwo);
      this.#postOpenFrameTwo = 0;
    }

    if (this.#announceFrame) {
      cancelAnimationFrame(this.#announceFrame);
      this.#announceFrame = 0;
    }
  }

  #handleDialogOpen = () => {
    if (this.#postOpenFrameOne) cancelAnimationFrame(this.#postOpenFrameOne);
    if (this.#postOpenFrameTwo) cancelAnimationFrame(this.#postOpenFrameTwo);

    this.#postOpenFrameOne = requestAnimationFrame(() => {
      this.#postOpenFrameOne = 0;

      this.#postOpenFrameTwo = requestAnimationFrame(() => {
        this.#postOpenFrameTwo = 0;

        if (!this.refs.dialog?.open) return;

        this.#updateStickyState();
        this.#handleHistoryOpen();
      });
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint() || !this.refs.dialog?.open) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();

    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    if (this.#postOpenFrameOne) {
      cancelAnimationFrame(this.#postOpenFrameOne);
      this.#postOpenFrameOne = 0;
    }

    if (this.#postOpenFrameTwo) {
      cancelAnimationFrame(this.#postOpenFrameTwo);
      this.#postOpenFrameTwo = 0;
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = null;

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    const dialog = this.refs.dialog;
    if (!dialog?.open) return;

    dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');

    try {
      await this.closeDialog();
    } finally {
      dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Opens immediately when a successful cart update event arrives.
   * ProductForm emits this event directly after /cart/add.js succeeds,
   * before its background /cart.js quantity synchronization.
   *
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;

    const dialog = this.refs.dialog;

    if (this.hasAttribute('auto-open') && !dialog?.open) {
      this.showDialog();
    }

    this.#scheduleCartCountAnnouncement(event.detail.resource?.item_count);
  };

  /**
   * Receives the authoritative cart total from the background cart sync
   * without re-triggering cart UI updates or incrementing the cart icon twice.
   *
   * @param {CustomEvent<{ item_count?: number }>} event
   */
  #handleCartCountSync = (event) => {
    const cartCount = event.detail?.item_count;
    if (typeof cartCount !== 'number') return;

    this.#scheduleCartCountAnnouncement(cartCount);
  };

  #scheduleCartCountAnnouncement(cartCount) {
    if (cartCount === undefined) return;

    if (this.#announceFrame) {
      cancelAnimationFrame(this.#announceFrame);
    }

    this.#announceFrame = requestAnimationFrame(() => {
      this.#announceFrame = 0;
      this.#announceCartCount(cartCount);
    });
  }

  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);

    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) {
      return;
    }

    const nextText = `${Theme.translations.cart_count}: ${cartCount}`;

    if (liveRegion.textContent !== nextText) {
      liveRegion.textContent = nextText;
    }
  }

  open() {
    if (!this.refs.dialog?.open) {
      this.showDialog();
    }

    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        requestAnimationFrame(() => {
          if (!this.refs.dialog?.open) return;

          const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
          const cta = installmentsContent?.querySelector('#shopify-installments-cta');

          cta?.addEventListener('click', this.closeDialog, { once: true });
        });
      });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);

    if (!dialog?.open) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = /** @type {HTMLElement | null} */ (dialog.querySelector('.cart-drawer__summary'));

    if (!content || !summary) {
      if (dialog.getAttribute('cart-summary-sticky') !== 'false') {
        dialog.setAttribute('cart-summary-sticky', 'false');
      }
      return;
    }

    const drawerHeight = dialog.clientHeight;
    const summaryHeight = summary.offsetHeight;

    if (!drawerHeight) return;

    const shouldBeSticky = summaryHeight / drawerHeight <= this.#summaryThreshold;
    const nextValue = shouldBeSticky ? 'true' : 'false';

    if (dialog.getAttribute('cart-summary-sticky') !== nextValue) {
      dialog.setAttribute('cart-summary-sticky', nextValue);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
